const AQI_RANGES = [
  { range: [0, 50], color: '#98D83F', face: 'healthy', key: 'good' },
  { range: [51, 100], color: '#FADB14', face: 'moderate', key: 'moderate' },
  { range: [101, 150], color: '#FE5E21', face: 'unhealthy', key: 'unhealthySensitive' },
  { range: [151, 200], color: '#DC0300', face: 'unhealthyRed', key: 'unhealthy' },
  { range: [201, 300], color: '#8A0B4C', face: 'veryUnhealthy', key: 'veryUnhealthy' },
  { range: [301, 500], color: '#82051C', face: 'hazardous', key: 'hazardous' },
];

const ICON_CODE_MAP = {
  0: 'windy',
  1: 'windy',
  2: 'windy',
  3: 'severeThunderstorm',
  4: 'thunderstorm',
  5: 'lightSnow',
  6: 'drizzle',
  7: 'lightSnow',
  8: 'drizzle',
  9: 'drizzle',
  10: 'freezingRain',
  11: 'lightRain',
  12: 'heavyRain',
  13: 'snowShowers',
  14: 'lightSnow',
  15: 'heavySnow',
  16: 'snow',
  17: 'icePellets',
  18: 'lightSnow',
  19: 'hazy',
  20: 'fog',
  21: 'hazy',
  22: 'smoke',
  23: 'blustery',
  24: 'windy',
  25: 'fog',
  26: 'mostlyCloudy',
  27: 'mostlyCloudy',
  28: 'mostlyCloudy',
  29: 'partlyCloudy',
  30: 'partlyCloudy',
  31: 'clear',
  32: 'sunny',
  33: 'mostlySunny',
  34: 'mostlySunny',
  35: 'rainThunder',
  36: 'sunny',
  37: 'thunderstorm',
  38: 'thunderstorm',
  39: 'scatteredShower',
  40: 'rainThunder',
  41: 'snowShowers',
  42: 'heavySnow',
  43: 'snowShowers',
  44: 'snowShowers',
  45: 'thunderstorm',
  46: 'snowShowers',
  47: 'thunderstorm',
};

const POLLEN_LEVELS = ['NONE', 'LOW', 'MODERATE', 'HIGH'];

function detectInputType(input) {
  const usZip = /^\d{5}(-\d{4})?$/;
  const caPostal = /^[ABCEGHJKLMNPRSTVWXYZ]\d[ABCEGHJKLMNPRSTVWXYZ](\s?\d[ABCEGHJKLMNPRSTVWXYZ]\d)?$/i;
  if (usZip.test(input)) return 'zip';
  if (caPostal.test(input)) return 'zip';
  return 'invalid';
}

function buildUrl(base, params) {
  const qs = new URLSearchParams(params).toString();
  return `${base}${base.includes('?') ? '&' : '?'}${qs}`;
}

function getAQIBlockIndex(aqi) {
  for (let i = 0; i < AQI_RANGES.length; i++) {
    const [min, max] = AQI_RANGES[i].range;
    if (aqi >= min && aqi <= max) return i;
  }
  return -1;
}

function getNextThreeDays() {
  const options = { month: 'long', day: 'numeric' };
  const locale = (typeof document !== 'undefined' && document.documentElement.lang) || 'en-US';
  return Array.from({ length: 3 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    return { label: date.toLocaleDateString(locale, options), index: i, value: i };
  });
}

function cleanDaypartNulls(weatherData) {
  if (!weatherData?.daypart?.[0]) return weatherData;
  const daypart = weatherData.daypart[0];
  const cleaned = {};
  for (const key of Object.keys(daypart)) {
    cleaned[key] = Array.isArray(daypart[key])
      ? daypart[key].filter((item) => item !== null)
      : daypart[key];
  }
  return { ...weatherData, daypart: [cleaned] };
}

function getInitialWeatherDayIndex(weatherData) {
  const temps = weatherData?.daypart?.[0]?.temperature || [];
  const hour = new Date().getHours();
  const isDay = hour >= 6 && hour < 18;
  if (temps.length === 7) return 0;
  if (temps.length === 8) return isDay ? 0 : 1;
  return 0;
}

function getDayOffset(index, temps) {
  if (temps?.length === 7) {
    if (index === 0) return 0;
    return Math.floor((index - 1) / 2) + 1;
  }
  if (temps?.length === 8) return Math.floor(index / 2);
  return 0;
}

function pollenCategoryValue(category) {
  switch (String(category).toLowerCase()) {
    case 'none': return 0;
    case 'low': return 1;
    case 'moderate': return 2;
    case 'high': return 3;
    default: return -1;
  }
}

function calculateOverallPollenLevel(data, index = 0) {
  if (!data) return 'UNKNOWN';
  const grass = pollenCategoryValue(data?.grassPollenCategory?.[index] || 'none');
  const tree = pollenCategoryValue(data?.treePollenCategory?.[index] || 'none');
  const ragweed = pollenCategoryValue(data?.ragweedPollenCategory?.[index] || 'none');
  const max = Math.max(grass, tree, ragweed);
  return POLLEN_LEVELS[max] || 'UNKNOWN';
}

class AqiWidget extends HTMLElement {
  constructor() {
    super();
    this.state = {
      loading: true,
      location: '',
      airQuality: null,
      weather: null,
      locationData: null,
      pollenCount: null,
      currentWeatherDay: 0,
    };
    this.icons = null;
    this.i18n = null;
  }

  connectedCallback() {
    if (this.hasInitialized) return;
    this.hasInitialized = true;

    this.loadI18nConfig();
    this.loadIconConfig();
    this.collectRefs();
    this.bindEvents();
    this.populateDayOptions();
    this.fetchDefaultLocation();
  }

  loadI18nConfig() {
    const script = this.querySelector('script[data-aqi-i18n]');
    if (script) {
      try {
        this.i18n = JSON.parse(script.textContent);
        return;
      } catch (e) {
        console.error('[aqi-widget] Failed to parse i18n config', e);
      }
    }
    this.i18n = {
      scale: {
        good: this.dataset.i18nScaleGood,
        moderate: this.dataset.i18nScaleModerate,
        unhealthySensitive: this.dataset.i18nScaleUnhealthySensitive,
        unhealthy: this.dataset.i18nScaleUnhealthy,
        veryUnhealthy: this.dataset.i18nScaleVeryUnhealthy,
        hazardous: this.dataset.i18nScaleHazardous,
      },
      errors: {
        enterZip: this.dataset.i18nErrorEnterZip,
        invalidZip: this.dataset.i18nErrorInvalidZip,
        locationNotFound: this.dataset.i18nErrorLocationNotFound,
        fetchFailed: this.dataset.i18nErrorFetchFailed,
      },
      alt: {
        weatherCondition: this.dataset.i18nWeatherConditionAlt,
        airQuality: this.dataset.i18nAirQuality,
      },
      units: {
        wind: this.dataset.i18nWindUnit,
      },
    };
  }

  getScaleLabel(key) {
    return this.i18n?.scale?.[key] || key;
  }

  getScaleRange(index) {
    const r = AQI_RANGES[index] || AQI_RANGES[0];
    return {
      ...r,
      label: this.getScaleLabel(r.key),
    };
  }

  loadIconConfig() {
    const script = this.querySelector('script[data-aqi-icons]');
    if (script) {
      try {
        this.icons = JSON.parse(script.textContent);
      } catch (e) {
        console.error('[aqi-widget] Failed to parse icon config', e);
      }
    }
  }

  collectRefs() {
    this.refs = {};
    for (const el of this.querySelectorAll('[ref]')) {
      this.refs[el.getAttribute('ref')] = el;
    }
  }

  bindEvents() {
    this.refs.searchForm?.addEventListener('submit', (e) => this.handleSubmit(e));
    this.refs.locateBtn?.addEventListener('click', () => this.fetchDefaultLocation());
    this.refs.zipInput?.addEventListener('input', (e) => {
      const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
      this.state.location = cleaned.trim();
      e.target.value = this.state.location;
    });
    this.refs.daySelect?.addEventListener('change', (e) => this.handleDayChange(e));
  }

  populateDayOptions() {
    const days = getNextThreeDays();
    const select = this.refs.daySelect;
    if (!select) return;
    select.innerHTML = '';
    for (const day of days) {
      const opt = document.createElement('option');
      opt.value = String(day.index);
      opt.textContent = day.label;
      select.appendChild(opt);
    }
  }

  showError(message) {
    const region = this.refs.errorRegion;
    if (!region) return;
    region.textContent = message;
    region.hidden = !message;
  }

  showLoading(isLoading) {
    this.state.loading = isLoading;
    if (this.refs.skeleton) this.refs.skeleton.hidden = !isLoading;
    if (this.refs.content) this.refs.content.hidden = isLoading;
  }

  handleSubmit(event) {
    event.preventDefault();
    const value = this.state.location?.trim();
    if (!value) {
      this.showError(this.i18n?.errors?.enterZip || '');
      return;
    }
    if (detectInputType(value) === 'invalid') {
      this.showError(this.i18n?.errors?.invalidZip || '');
      return;
    }
    this.showError('');
    this.fetchCoordinatesFromInput(value);
  }

  handleDayChange(event) {
    const dayOffset = Number(event.target.value);
    const hour = new Date().getHours();
    const isDay = hour >= 6 && hour < 18;
    const temps = this.state.weather?.daypart?.[0]?.temperature || [];

    let finalIndex = 0;
    if (temps.length === 7) {
      if (dayOffset === 0) {
        finalIndex = 0;
      } else {
        const baseIndex = (dayOffset - 1) * 2 + 1;
        finalIndex = baseIndex + (isDay ? 0 : 1);
      }
    } else if (temps.length === 8) {
      finalIndex = dayOffset * 2 + (isDay ? 0 : 1);
    }

    finalIndex = Math.max(0, Math.min(finalIndex, temps.length - 1));
    this.state.currentWeatherDay = finalIndex;
    this.renderForecast();
  }

  async fetchDefaultLocation() {
    const fallback = () => this.fetchCoordinatesFromInput(this.dataset.defaultZip || '10001');

    if (!navigator.geolocation) {
      await fallback();
      return;
    }

    this.showLoading(true);
    this.showError('');

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        await this.fetchDataUsingLatLon(coords.latitude, coords.longitude);
      },
      async () => {
        await fallback();
      },
      { timeout: 8000 }
    );
  }

  async fetchCoordinatesFromInput(input) {
    this.showLoading(true);
    this.showError('');
    try {
      const url = buildUrl(`${this.dataset.geoApiBase}/geocode/json`, {
        address: input,
        key: this.dataset.geoApiKey,
        components: 'country:US|country:CA',
      });
      const response = await fetch(url);
      if (!response.ok) throw new Error('geocode_failed');
      const data = await response.json();
      const loc = data?.results?.[0]?.geometry?.location;
      if (!loc) throw new Error('invalid_location');
      await this.fetchDataUsingLatLon(loc.lat, loc.lng);
      this.state.currentWeatherDay = 0;
      if (this.refs.daySelect) this.refs.daySelect.value = '0';
      this.renderForecast();
    } catch (err) {
      console.error('[aqi-widget]', err);
      this.showError(this.i18n?.errors?.locationNotFound || '');
      this.showLoading(false);
    }
  }

  async fetchDataUsingLatLon(lat, lon) {
    try {
      const apiKey = this.dataset.weatherApiKey;
      const geoApiKey = this.dataset.geoApiKey;
      const weatherBase = this.dataset.weatherApiBase;
      const pollenBase = this.dataset.pollenApiBase;
      const geoBase = this.dataset.geoApiBase;

      const [airQualityRes, weatherRes, locationRes, pollenRes] = await Promise.all([
        fetch(buildUrl(`${weatherBase}/wx/globalAirQuality`, {
          apiKey, format: 'json', language: 'en-US', scale: 'EPA', geocode: `${lat},${lon}`,
        })).then((r) => r.json()),
        fetch(buildUrl(`${weatherBase}/wx/forecast/daily/3day`, {
          apiKey, format: 'json', language: 'en-US', units: 'e', geocode: `${lat},${lon}`,
        })).then((r) => r.json()),
        fetch(buildUrl(`${geoBase}/geocode/json`, {
          latlng: `${lat},${lon}`, key: geoApiKey, language: 'en-US', component: 'country:US|country:CA',
        })).then((r) => r.json()),
        fetch(buildUrl(`${pollenBase}/indices/pollen/daypart/15day`, {
          apiKey, format: 'json', language: 'en-US', geocode: `${lat},${lon}`,
        })).then((r) => r.json()).catch(() => null),
      ]);

      this.state.airQuality = airQualityRes?.globalairquality || null;
      this.state.weather = cleanDaypartNulls(weatherRes);
      this.state.currentWeatherDay = getInitialWeatherDayIndex(this.state.weather);

      const components = locationRes?.results?.[0]?.address_components || [];
      this.state.locationData =
        components.find((c) => c.types.includes('locality'))?.long_name ||
        components.find((c) => c.types.includes('administrative_area_level_1'))?.long_name ||
        components.find((c) => c.types.includes('country'))?.long_name || '';

      const pollenForecast = pollenRes?.pollenForecast12hour || null;
      this.state.pollenCount = calculateOverallPollenLevel(pollenForecast, 0);

      this.render();
    } catch (err) {
      console.error('[aqi-widget]', err);
      this.showError(this.i18n?.errors?.fetchFailed || '');
    } finally {
      this.showLoading(false);
    }
  }

  getWeatherIconUrl(iconCode) {
    const iconName = ICON_CODE_MAP[iconCode] || 'sunny';
    return this.icons?.weather?.[iconName] || this.icons?.weather?.sunny || '';
  }

  getFaceIconUrl(faceKey) {
    return this.icons?.faces?.[faceKey] || '';
  }

  renderAqiBar(aqi) {
    const activeIndex = getAQIBlockIndex(aqi);
    const html = AQI_RANGES.map((r, i) => {
      const arrow = i === activeIndex
        ? '<span class="aqi-widget__bar-arrow" aria-hidden="true"></span>'
        : '';
      return `<span class="aqi-widget__bar-block" style="background-color:${r.color}">${arrow}</span>`;
    }).join('');
    this.refs.aqiBar.innerHTML = html;
  }

  renderAqi() {
    const aq = this.state.airQuality;
    if (!aq) return;
    const aqi = aq.airQualityIndex ?? 0;
    const idx = Math.max(0, getAQIBlockIndex(aqi));
    const range = this.getScaleRange(idx);

    this.refs.aqiValue.textContent = aqi;
    const category = (range.label || aq.airQualityCategory || '').split(' ')[0];
    this.refs.aqiCategory.innerHTML = `<strong>${category}</strong>`;

    const pm25 = aq.pollutants?.['PM2.5']?.amount;
    const pollutant = aq.primaryPollutant || '';
    this.refs.aqiPollutant.innerHTML = `
      <span>${pollutant}</span>
      <span class="aqi-widget__pollutant-divider">|</span>
      <span style="white-space:nowrap">${pm25 ?? '--'}ug/m<sup>3</sup></span>
    `;

    const faceUrl = this.getFaceIconUrl(range.face);
    const airQualitySuffix = this.i18n?.alt?.airQuality || '';
    const faceAlt = airQualitySuffix ? `${range.label} ${airQualitySuffix}` : range.label;
    this.refs.aqiFace.innerHTML = faceUrl
      ? `<img src="${faceUrl}" alt="${faceAlt}" width="73" height="73" loading="lazy">`
      : '';

    this.renderAqiBar(aqi);

    const ozone = aq.pollutants?.O3?.amount;
    this.refs.ozoneValue.textContent = ozone != null ? ozone : '--';
  }

  renderForecast() {
    const w = this.state.weather;
    const day = w?.daypart?.[0];
    const i = this.state.currentWeatherDay;
    if (!day) return;

    const iconCode = day.iconCode?.[i];
    const iconUrl = this.getWeatherIconUrl(iconCode);
    const weatherAlt = this.i18n?.alt?.weatherCondition || '';
    this.refs.weatherIcon.innerHTML = iconUrl
      ? `<img src="${iconUrl}" alt="${weatherAlt}" width="40" height="40" loading="lazy">`
      : '';

    const temp = day.temperature?.[i];
    this.refs.tempValue.textContent = temp != null ? `${temp}°` : '--°';
    this.refs.tempLabel.textContent = day.wxPhraseLong?.[i] || '';

    const humidity = day.relativeHumidity?.[i];
    this.refs.humidityValue.textContent = humidity != null ? `${humidity}%` : '--';

    const wind = day.windSpeed?.[i];
    const windUnit = this.i18n?.units?.wind || '';
    this.refs.windValue.textContent = wind != null ? (windUnit ? `${wind} ${windUnit}` : `${wind}`) : (windUnit ? `-- ${windUnit}` : '--');

    const temps = day.temperature || [];
    const dayOffset = getDayOffset(i, temps);
    if (this.refs.daySelect) this.refs.daySelect.value = String(dayOffset);
  }

  render() {
    this.refs.cityLabel.textContent = this.state.locationData || '';
    this.renderAqi();
    this.renderForecast();
    this.refs.pollenValue.textContent = this.state.pollenCount || '--';
  }
}

if (!customElements.get('aqi-widget')) {
  customElements.define('aqi-widget', AqiWidget);
}
